import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";
import { API_BASE_URL } from "../config/api";

import "./catalog-wow.css";

const Parts = () => {
  const navigate = useNavigate();
  const { lang } = useLanguage();

  const [parts, setParts] = useState([]);
  const [showFilters, setShowFilters] = useState(false);

  const ALL = "All";
  const PARTS_PER_PAGE = 16;

  const [currentPage, setCurrentPage] = useState(1);

  const [filters, setFilters] = useState({
    category: ALL,
    brands: [],
    models: [],
    price: 2000,
    search: "",
  });

  // ================= TEXT =================
  const t = {
    sq: {
      search: "Kërko pjesë...",
      price: "Çmimi",
      max: "Maksimumi",
      category: "Kategoria",
      brand: "Marka",
      model: "Modeli",
      reset: "Rifillo filtrat",
      parts: "Pjesë",
      view: "Shiko",
      filters: "Filtrat",
      closeFilters: "Mbyll Filtrat",
      noResults: "Duke u ngarkuar...",
    },
    en: {
      search: "Search parts...",
      price: "Price",
      max: "Max",
      category: "Category",
      brand: "Brand",
      model: "Model",
      reset: "Reset filters",
      parts: "Parts",
      view: "View",
      filters: "Filters",
      closeFilters: "Close Filters",
      noResults: "Loading...",
    },
  };
  

  const text = t[lang] || t.sq;

  // ================= FETCH =================
  useEffect(() => {
    const fetchParts = async () => {
      try {
        const token = localStorage.getItem("authToken");

        const res = await fetch(`${API_BASE_URL}/api/parts`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });

        const data = await res.json();
        setParts(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error(err);
        setParts([]);
      }
    };

    fetchParts();
  }, []);

  // reset page on filter change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, parts]);

  // ================= FILTER LISTS =================
  const categories = useMemo(() => {
    return [ALL, ...new Set(parts.map((p) => p.category).filter(Boolean))];
  }, [parts]);

  const brands = useMemo(() => {
    let base = parts;

    if (filters.category !== ALL) {
      base = base.filter((p) => p.category === filters.category);
    }

    return [...new Set(base.map((p) => p.brand).filter(Boolean))];
  }, [parts, filters.category]);

  const models = useMemo(() => {
    let base = parts;

    if (filters.category !== ALL) {
      base = base.filter((p) => p.category === filters.category);
    }

    if (filters.brands.length > 0) {
      base = base.filter((p) => filters.brands.includes(p.brand));
    }

    return [...new Set(base.map((p) => p.model).filter(Boolean))];
  }, [parts, filters.category, filters.brands]);

  // ================= FILTERED =================
  const filteredParts = useMemo(() => {
    const search = filters.search.toLowerCase();

    return parts.filter((p) => {
      const categoryOk =
        filters.category === ALL || p.category === filters.category;

      const brandOk =
        filters.brands.length === 0 || filters.brands.includes(p.brand);

      const modelOk =
        filters.models.length === 0 || filters.models.includes(p.model);

      const priceOk = Number(p.price || 0) <= filters.price;

      const searchOk =
        !search ||
        p.name?.toLowerCase().includes(search) ||
        p.brand?.toLowerCase().includes(search) ||
        p.model?.toLowerCase().includes(search) ||
        p.category?.toLowerCase().includes(search);

      return categoryOk && brandOk && modelOk && priceOk && searchOk;
    });
  }, [parts, filters]);

  // ================= GROUP =================
  const grouped = useMemo(() => {
    const map = new Map();

    filteredParts.forEach((p) => {
      const key = `${p.category}-${p.brand}-${p.model}`;

      if (!map.has(key)) {
        map.set(key, {
          ...p,
          items: [p],
          count: 1,
        });
      } else {
        const existing = map.get(key);
        existing.items.push(p);
        existing.count += 1;
      }
    });

    return Array.from(map.values());
  }, [filteredParts]);

  // ================= PAGINATION =================
  const totalPages = Math.ceil(grouped.length / PARTS_PER_PAGE);

  const paginatedParts = useMemo(() => {
    const start = (currentPage - 1) * PARTS_PER_PAGE;
    return grouped.slice(start, start + PARTS_PER_PAGE);
  }, [grouped, currentPage]);

  // ================= RESET =================
  const resetFilters = () => {
    setFilters({
      category: ALL,
      brands: [],
      models: [],
      price: 2000,
      search: "",
    });
  };

return (
  <div className="min-h-screen bg-slate-50 px-3 py-4 md:px-5">
    {/* ================= MOBILE FILTER BUTTON ================= */}
    <div className="mb-4 lg:hidden">
      <button
        type="button"
        onClick={() => setShowFilters(!showFilters)}
        className="
          w-full
          rounded-xl
          bg-blue-600
          py-3
          text-sm
          font-bold
          text-white
          shadow-lg
          shadow-blue-200/70
          transition
          hover:bg-blue-700
        "
      >
        {showFilters ? text.closeFilters : text.filters}
      </button>
    </div>

    {/* ================= MAIN LAYOUT ================= */}
    <div
      className="
        mx-auto
        flex
        w-full
        max-w-[1800px]
        flex-col
        items-start
        gap-5
        lg:flex-row
      "
    >
      {/* ================= SIDEBAR ================= */}
      <aside
        className={`
          h-fit
          w-full
          self-start
          rounded-2xl
          border
          border-slate-200
          bg-white
          p-5
          shadow-[0_12px_35px_rgba(15,23,42,0.08)]

          lg:sticky
          lg:top-16
          lg:w-60
          lg:min-w-60

          ${showFilters ? "block" : "hidden"}
          lg:block
        `}
      >
        {/* SEARCH */}
        <input
          type="text"
          className="
            mb-4
            h-11
            w-full
            rounded-xl
            border
            border-slate-200
            bg-slate-50
            px-3
            text-sm
            text-slate-800
            outline-none
            transition
            placeholder:text-slate-400
            focus:border-blue-500
            focus:bg-white
            focus:ring-4
            focus:ring-blue-100
          "
          placeholder={text.search}
          value={filters.search}
          onChange={(e) =>
            setFilters({
              ...filters,
              search: e.target.value,
            })
          }
        />

        {/* ================= PRICE ================= */}
        <div className="border-b border-slate-100 pb-5">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-sm font-extrabold text-slate-900">
              {text.price}
            </h3>

            <span className="text-xs font-bold text-blue-600">
              €{filters.price.toLocaleString()}
            </span>
          </div>

          <input
            type="range"
            min="0"
            max="2000"
            value={filters.price}
            onChange={(e) =>
              setFilters({
                ...filters,
                price: Number(e.target.value),
              })
            }
            className="w-full cursor-pointer accent-blue-600"
          />

          <div className="mt-2 flex justify-between text-xs text-slate-500">
            <span>€0</span>
            <span>{text.max}</span>
          </div>
        </div>

        {/* ================= CATEGORY ================= */}
        <div className="border-b border-slate-100 py-5">
          <h3 className="mb-2 text-sm font-extrabold text-slate-900">
            {text.category}
          </h3>

          <div className="space-y-1">
            {categories.map((c) => (
              <label
                key={c}
                className="
                  flex
                  min-h-8
                  cursor-pointer
                  items-center
                  gap-2
                  rounded-lg
                  px-2
                  text-sm
                  font-medium
                  text-slate-600
                  transition
                  hover:bg-blue-50
                  hover:text-blue-700
                "
              >
                <input
                  type="radio"
                  checked={filters.category === c}
                  onChange={() =>
                    setFilters({
                      ...filters,
                      category: c,
                      brands: [],
                      models: [],
                    })
                  }
                  className="h-4 w-4 cursor-pointer accent-blue-600"
                />

                <span>{c}</span>
              </label>
            ))}
          </div>
        </div>

        {/* ================= BRAND ================= */}
        <div className="border-b border-slate-100 py-5">
          <h3 className="mb-2 text-sm font-extrabold text-slate-900">
            {text.brand}
          </h3>

          <div className="max-h-44 space-y-1 overflow-y-auto pr-1">
            {brands.map((b) => (
              <label
                key={b}
                className="
                  flex
                  min-h-8
                  cursor-pointer
                  items-center
                  gap-2
                  rounded-lg
                  px-2
                  text-sm
                  font-medium
                  text-slate-600
                  transition
                  hover:bg-blue-50
                  hover:text-blue-700
                "
              >
                <input
                  type="checkbox"
                  checked={filters.brands.includes(b)}
                  onChange={() =>
                    setFilters((prev) => ({
                      ...prev,
                      brands: prev.brands.includes(b)
                        ? prev.brands.filter((x) => x !== b)
                        : [...prev.brands, b],
                    }))
                  }
                  className="
                    h-4
                    w-4
                    cursor-pointer
                    rounded
                    accent-blue-600
                  "
                />

                <span>{b}</span>
              </label>
            ))}
          </div>
        </div>

        {/* ================= MODEL ================= */}
        <div className="pt-5">
          <h3 className="mb-2 text-sm font-extrabold text-slate-900">
            {text.model}
          </h3>

          <div className="max-h-44 space-y-1 overflow-y-auto pr-1">
            {models.map((m) => (
              <label
                key={m}
                className="
                  flex
                  min-h-8
                  cursor-pointer
                  items-center
                  gap-2
                  rounded-lg
                  px-2
                  text-sm
                  font-medium
                  text-slate-600
                  transition
                  hover:bg-blue-50
                  hover:text-blue-700
                "
              >
                <input
                  type="checkbox"
                  checked={filters.models.includes(m)}
                  onChange={() =>
                    setFilters((prev) => ({
                      ...prev,
                      models: prev.models.includes(m)
                        ? prev.models.filter((x) => x !== m)
                        : [...prev.models, m],
                    }))
                  }
                  className="
                    h-4
                    w-4
                    cursor-pointer
                    rounded
                    accent-blue-600
                  "
                />

                <span>{m}</span>
              </label>
            ))}
          </div>
        </div>

        {/* ================= RESET ================= */}
        <button
          type="button"
          onClick={resetFilters}
          className="
            mt-6
            w-full
            rounded-xl
            border
            border-slate-200
            bg-slate-100
            py-2.5
            text-sm
            font-bold
            text-slate-700
            transition
            hover:border-blue-600
            hover:bg-blue-600
            hover:text-white
          "
        >
          {text.reset}
        </button>
      </aside>

      {/* ================= GRID ================= */}
      <div className="min-w-0 flex-1">
        <div className="mb-4 flex items-center justify-between">
          <h1 className="text-xl font-extrabold text-slate-900 md:text-2xl">
            {text.parts}
          </h1>

          <span
            className="
              rounded-full
              border
              border-blue-100
              bg-blue-50
              px-3
              py-1
              text-xs
              font-bold
              text-blue-700
            "
          >
            {grouped.length}
          </span>
        </div>

        {/* ================= PARTS GRID ================= */}
        <div
          className="
            grid
            grid-cols-2
            gap-4

            sm:grid-cols-3
            lg:grid-cols-4
            xl:grid-cols-5
            2xl:grid-cols-6
          "
        >
          {paginatedParts.map((p) => (
            <div
              key={p.id}
              onClick={() =>
                navigate(`/parts/details/${p.items[0].id}`)
              }
              className="
                group
                flex
                min-h-[320px]
                cursor-pointer
                flex-col
                overflow-hidden
                rounded-2xl
                border
                border-slate-200
                bg-white
                p-3
                shadow-[0_6px_20px_rgba(15,23,42,0.06)]
                transition
                duration-300
                hover:-translate-y-1
                hover:border-blue-200
                hover:shadow-[0_16px_35px_rgba(37,99,235,0.12)]
              "
            >
              {/* IMAGE */}
              <div
                className="
                  flex
                  h-32
                  items-center
                  justify-center
                  overflow-hidden
                  rounded-xl
                  bg-slate-50
                  p-2
                  md:h-36
                "
              >
                <img
                  src={
                    p.image
                      ? `${API_BASE_URL}${p.image}`
                      : "https://placehold.co/300x300"
                  }
                  className="
                    h-full
                    w-full
                    object-contain
                    transition
                    duration-300
                    group-hover:scale-105
                  "
                  alt={p.name}
                  loading="lazy"
                />
              </div>

              {/* CONTENT */}
              <div className="flex flex-1 flex-col pt-3">
                {p.brand && (
                  <span
                    className="
                      mb-1
                      text-[9px]
                      font-extrabold
                      uppercase
                      tracking-[0.08em]
                      text-slate-400
                    "
                  >
                    {p.brand}
                  </span>
                )}

                <h3
                  className="
                    line-clamp-2
                    min-h-[38px]
                    text-xs
                    font-bold
                    leading-5
                    text-slate-900
                    md:text-sm
                  "
                >
                  {p.name}
                </h3>

                <p
                  className="
                    mt-1
                    line-clamp-2
                    text-[10px]
                    leading-4
                    text-slate-500
                  "
                >
                  {p.category} • {p.brand} • {p.model}
                </p>

                <p
                  className="
                    mt-2
                    text-base
                    font-extrabold
                    text-slate-900
                  "
                >
                  €{Number(p.price || 0).toFixed(2)}
                </p>

                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/parts/details/${p.items[0].id}`);
                  }}
                  className="
                    mt-auto
                    w-full
                    rounded-xl
                    bg-green-600
                    py-2
                    text-xs
                    font-bold
                    text-white
                    transition
                    hover:bg-green-700
                    md:text-sm
                  "
                >
                  {text.view}
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* ================= PAGINATION ================= */}
        {totalPages > 1 && (
          <div className="mt-10 flex flex-wrap items-center justify-center gap-2">
            <button
              type="button"
              disabled={currentPage === 1}
              onClick={() =>
                setCurrentPage((p) => Math.max(p - 1, 1))
              }
              className="
                flex
                h-10
                items-center
                justify-center
                rounded-xl
                border
                border-slate-200
                bg-white
                px-3
                text-sm
                font-bold
                text-slate-700
                transition
                hover:border-blue-600
                hover:bg-blue-600
                hover:text-white
                disabled:cursor-not-allowed
                disabled:opacity-40
              "
            >
              Prev
            </button>

            {Array.from(
              { length: totalPages },
              (_, i) => i + 1
            ).map((num) => (
              <button
                type="button"
                key={num}
                onClick={() => setCurrentPage(num)}
                className={`
                  flex
                  h-10
                  min-w-10
                  items-center
                  justify-center
                  rounded-xl
                  border
                  px-3
                  text-sm
                  font-bold
                  transition

                  ${
                    currentPage === num
                      ? `
                        border-blue-600
                        bg-blue-600
                        text-white
                      `
                      : `
                        border-slate-200
                        bg-white
                        text-slate-700
                        hover:border-blue-600
                        hover:bg-blue-600
                        hover:text-white
                      `
                  }
                `}
              >
                {num}
              </button>
            ))}

            <button
              type="button"
              disabled={currentPage === totalPages}
              onClick={() =>
                setCurrentPage((p) =>
                  Math.min(p + 1, totalPages)
                )
              }
              className="
                flex
                h-10
                items-center
                justify-center
                rounded-xl
                border
                border-slate-200
                bg-white
                px-3
                text-sm
                font-bold
                text-slate-700
                transition
                hover:border-blue-600
                hover:bg-blue-600
                hover:text-white
                disabled:cursor-not-allowed
                disabled:opacity-40
              "
            >
              Next
            </button>
          </div>
        )}

        {/* ================= EMPTY ================= */}
        {grouped.length === 0 && (
          <div
            className="
              mt-6
              rounded-2xl
              border
              border-slate-200
              bg-white
              p-10
              text-center
              font-semibold
              text-slate-500
              shadow-sm
            "
          >
            {text.noResults}
          </div>
        )}
      </div>
    </div>
  </div>
);
};

export default Parts;